/**
 * LogEntriesPanelInternal Component
 *
 * Internal panel component displaying log entries with processing time.
 * Extracted from EngineOutputInternalSection for reuse in tab-based layouts.
 * This is an internal/debug component (not user-facing).
 */

import { Text, Subheading, Strong } from "@a7y/tailwind-catalyst";
import { DevCardBadge } from "./DevCardBadge";

/**
 * Generic log entry interface that works with all engines.
 * All engines provide timestamp and action fields.
 */
export interface EngineLogEntry {
  timestamp: string | { __time__: string };
  action: string;
}

export interface LogEntriesPanelInternalProps {
  /** Log entries to display */
  log?: EngineLogEntry[];
  /** Processing time in milliseconds */
  processingTimeMs?: number;
}

export function LogEntriesPanelInternal({ log, processingTimeMs }: LogEntriesPanelInternalProps) {
  return (
    <DevCardBadge name="LogEntriesPanelInternal">
    <div className="space-y-4">
      {/* Processing Time */}
      {processingTimeMs !== undefined && (
        <div className="bg-gray-50 dark:bg-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Text>
              <Strong>Processing Time:</Strong>
            </Text>
            <span className="text-lg font-semibold text-zinc-950 dark:text-white">
              {(processingTimeMs / 1000).toFixed(2)}s
            </span>
            <Text className="text-gray-500">({processingTimeMs.toLocaleString()}ms)</Text>
          </div>
        </div>
      )}

      {/* Log Entries */}
      <div className="border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
        <div className="bg-gray-100 dark:bg-zinc-800 px-4 py-2 border-b border-gray-200 dark:border-zinc-700">
          <Subheading level={4}>Log Entries ({log?.length ?? 0})</Subheading>
        </div>
        <div className="overflow-x-auto">
          {log && log.length > 0 ? (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-zinc-800/50 border-b border-gray-200 dark:border-zinc-700">
                  <th className="py-2 px-4 text-left font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    Time
                  </th>
                  <th className="py-2 px-4 text-left font-medium text-gray-500 dark:text-gray-400">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {log.map((entry, index) => (
                  <LogRow key={index} entry={entry} />
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-4">
              <Text className="text-gray-500 dark:text-gray-400 italic">No log entries available</Text>
            </div>
          )}
        </div>
      </div>
    </div>
    </DevCardBadge>
  );
}

// ======================================================================================|
// MARK: LogRow

interface LogRowProps {
  entry: EngineLogEntry;
}

function LogRow({ entry }: LogRowProps) {
  // Handle both string timestamps and { __time__: string } format
  const timestampStr =
    typeof entry.timestamp === "string" ? entry.timestamp : entry.timestamp.__time__;

  const timestamp = new Date(timestampStr);
  const timeStr = timestamp.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const msStr = timestamp.getMilliseconds().toString().padStart(3, "0");

  return (
    <tr className="border-b border-gray-100 dark:border-zinc-800 last:border-b-0">
      <td className="py-1.5 px-4 text-gray-400 dark:text-gray-500 font-mono text-xs whitespace-nowrap">
        {timeStr}.{msStr}
      </td>
      <td className="py-1.5 px-4 text-gray-700 dark:text-gray-300">{entry.action}</td>
    </tr>
  );
}
